import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Music,
  Music2,
  Captions,
  CaptionsOff,
  Maximize2,
  Crop,
  Mic,
  Type,
} from "lucide-react";
import { SCENES, OFFSETS, BRAND, TE } from "@/data/adPackage";
import SceneOverlay from "@/components/SceneOverlay";
import { MusicEngine } from "@/lib/music";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function audioKeyFor(lang, sceneId) {
  return lang === "te" ? `te_${sceneId}` : sceneId;
}

function indexAt(t) {
  for (let i = 0; i < SCENES.length; i++) {
    const start = OFFSETS.map[SCENES[i].id];
    if (t < start + SCENES[i].dur) return i;
  }
  return SCENES.length - 1;
}

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function AdPlayer({ lang = "en" }) {
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [musicOn, setMusicOn] = useState(false);
  const [subs, setSubs] = useState(true);
  const [mode, setMode] = useState("vo"); // 'vo' | 'novo'
  const [guide, setGuide] = useState(false);
  const [t, setT] = useState(0);

  const tRef = useRef(0);
  const lastRef = useRef(0);
  const rafRef = useRef(null);
  const audioMapRef = useRef(null);
  const musicRef = useRef(null);
  const containerRef = useRef(null);

  const currentIndex = indexAt(t);
  const scene = SCENES[currentIndex];

  // init audio + music once
  useEffect(() => {
    const map = {};
    SCENES.forEach((s) => {
      const key = audioKeyFor(lang, s.id);
      const a = new Audio(`${API}/media/${key}`);
      a.preload = "auto";
      map[s.id] = a;
    });
    audioMapRef.current = map;
    musicRef.current = new MusicEngine();
    return () => {
      Object.values(map).forEach((a) => a.pause());
      if (musicRef.current) musicRef.current.dispose();
    };
  }, [lang]);

  // rAF timeline
  useEffect(() => {
    if (!playing) return;
    lastRef.current = performance.now();
    const tick = (now) => {
      const dt = (now - lastRef.current) / 1000;
      lastRef.current = now;
      tRef.current += dt;
      if (tRef.current >= OFFSETS.total) {
        tRef.current = OFFSETS.total;
        setT(OFFSETS.total);
        setPlaying(false);
        setEnded(true);
        return;
      }
      setT(tRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  // voice-over audio per scene
  useEffect(() => {
    const audios = audioMapRef.current;
    if (!audios) return;
    Object.values(audios).forEach((a) => a.pause());
    if (playing && mode === "vo" && !muted) {
      const a = audios[SCENES[currentIndex].id];
      if (a) {
        try {
          a.currentTime = 0;
        } catch (e) {}
        a.play().catch(() => {});
      }
    }
  }, [currentIndex, playing, mode, muted]);

  // music
  useEffect(() => {
    const m = musicRef.current;
    if (!m) return;
    m.setEnabled(musicOn && !muted);
    if (playing) m.start();
    else m.pause();
  }, [playing, musicOn, muted]);

  const togglePlay = () => {
    if (ended || tRef.current >= OFFSETS.total) {
      tRef.current = 0;
      setT(0);
      setEnded(false);
    }
    setStarted(true);
    setPlaying((p) => !p);
  };

  const restart = () => {
    tRef.current = 0;
    setT(0);
    setEnded(false);
    lastRef.current = performance.now();
    if (!playing) {
      setStarted(true);
      setPlaying(true);
    }
  };

  const seekTo = (index) => {
    tRef.current = OFFSETS.map[SCENES[index].id];
    setT(tRef.current);
    setEnded(false);
    lastRef.current = performance.now();
    setStarted(true);
  };

  const goFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else if (el.requestFullscreen) el.requestFullscreen();
  };

  const progress = (t / OFFSETS.total) * 100;
  const subtitleText =
    lang === "te"
      ? mode === "vo"
        ? TE[scene.id].vo
        : TE[scene.id].caption
      : mode === "vo"
      ? scene.vo
      : scene.caption;

  const ctrlBtn =
    "flex items-center justify-center rounded-full text-white/90 hover:text-white transition-colors duration-200";

  return (
    <div
      ref={containerRef}
      data-testid="ad-player"
      className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_80px_rgba(0,0,0,0.6)] grain select-none"
    >
      {/* Background image with Ken Burns + crossfade */}
      <AnimatePresence>
        <motion.div
          key={scene.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center kenburns"
            style={{ backgroundImage: `url(${scene.image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/25" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/30" />
        </motion.div>
      </AnimatePresence>

      {/* Scene overlay motion graphics */}
      <div className="absolute inset-0">
        <SceneOverlay key={`ov-${scene.id}`} scene={scene} />
      </div>

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-4 sm:px-6 py-3 bg-gradient-to-b from-black/60 to-transparent">
        <div className="flex items-center gap-2">
          <img src={BRAND.logo} alt="BiteGoFood" className="w-7 h-7 rounded-lg" />
          <span className="text-[10px] sm:text-xs font-semibold tracking-[0.2em] text-white/80 uppercase">
            16:9 Master{lang === "te" ? " · తెలుగు" : ""}
          </span>
        </div>
        <span
          data-testid="player-mode-badge"
          className="text-[9px] sm:text-[11px] font-bold tracking-[0.18em] uppercase px-2.5 py-1 rounded-full glass text-orange-300"
        >
          {lang === "te"
            ? mode === "vo"
              ? "తెలుగు వాయిస్-ఓవర్"
              : "తెలుగు · టెక్స్ట్"
            : mode === "vo"
            ? "Voice-Over"
            : "No VO · Text-Led"}
        </span>
      </div>

      {/* 9:16 safe guide */}
      {guide && (
        <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center">
          <div className="h-full" style={{ width: "31.64%" }}>
            <div className="w-full h-full border-2 border-dashed border-orange-400/80 relative">
              <span className="absolute -top-0 left-1/2 -translate-x-1/2 translate-y-2 text-[9px] sm:text-[11px] font-bold tracking-widest uppercase text-orange-300 bg-black/60 px-2 py-0.5 rounded">
                9:16 Safe
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Subtitles */}
      {subs && started && (
        <div className="absolute inset-x-0 bottom-16 sm:bottom-20 z-30 flex justify-center px-6 pointer-events-none">
          <span className="max-w-2xl text-center text-white text-sm sm:text-lg font-medium leading-snug bg-black/55 backdrop-blur-sm px-3 py-1.5 rounded-md">
            {subtitleText}
          </span>
        </div>
      )}

      {/* Big center play / replay */}
      {(!playing || ended) && (
        <button
          data-testid="player-center-play"
          onClick={togglePlay}
          className="absolute inset-0 z-40 flex items-center justify-center group"
          aria-label={ended ? "Replay commercial" : "Play commercial"}
        >
          <span className="w-16 h-16 sm:w-24 sm:h-24 rounded-full brand-gradient flex items-center justify-center shadow-2xl transition-transform duration-200 group-hover:scale-110">
            {ended ? (
              <RotateCcw className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
            ) : (
              <Play className="w-7 h-7 sm:w-10 sm:h-10 text-white translate-x-0.5" fill="white" />
            )}
          </span>
        </button>
      )}

      {/* Controls bar */}
      <div className="absolute bottom-0 inset-x-0 z-40 px-3 sm:px-5 pb-3 pt-8 bg-gradient-to-t from-black/85 to-transparent">
        {/* progress */}
        <div className="relative mb-2">
          <div className="h-1.5 w-full rounded-full bg-white/20 overflow-hidden">
            <div className="h-full brand-gradient" style={{ width: `${progress}%` }} />
          </div>
          {SCENES.map((s, i) => {
            const left = (OFFSETS.map[s.id] / OFFSETS.total) * 100;
            return (
              <button
                key={s.id}
                data-testid={`player-chapter-${i + 1}`}
                onClick={() => seekTo(i)}
                title={`Scene ${i + 1}: ${s.chapter}`}
                className="absolute -top-1 w-3.5 h-3.5 -ml-1.5 rounded-full border-2 border-black bg-white/70 hover:bg-orange-400 transition-colors duration-200"
                style={{ left: `${left}%` }}
              />
            );
          })}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3 text-white">
          <button data-testid="player-play" onClick={togglePlay} className={`${ctrlBtn} w-9 h-9`} aria-label="Play/Pause">
            {playing ? <Pause className="w-5 h-5" fill="white" /> : <Play className="w-5 h-5" fill="white" />}
          </button>
          <button data-testid="player-restart" onClick={restart} className={`${ctrlBtn} w-8 h-8`} aria-label="Restart">
            <RotateCcw className="w-4 h-4" />
          </button>
          <button data-testid="player-mute" onClick={() => setMuted((m) => !m)} className={`${ctrlBtn} w-8 h-8`} aria-label="Mute">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button
            data-testid="player-music"
            onClick={() => setMusicOn((m) => !m)}
            className={`${ctrlBtn} w-8 h-8 ${musicOn ? "text-orange-400" : ""}`}
            aria-label="Toggle music"
          >
            {musicOn ? <Music className="w-4 h-4" /> : <Music2 className="w-4 h-4" />}
          </button>
          <button
            data-testid="player-subs"
            onClick={() => setSubs((s) => !s)}
            className={`${ctrlBtn} w-8 h-8 ${subs ? "text-orange-400" : ""}`}
            aria-label="Toggle subtitles"
          >
            {subs ? <Captions className="w-4 h-4" /> : <CaptionsOff className="w-4 h-4" />}
          </button>

          <span className="text-[11px] sm:text-xs tabular-nums text-white/70 ml-1">
            {fmtTime(t)} / {fmtTime(OFFSETS.total)}
          </span>

          <div className="flex-1" />

          {/* Version toggle */}
          <div className="flex items-center rounded-full glass p-0.5">
            <button
              data-testid="player-mode-vo"
              onClick={() => setMode("vo")}
              className={`flex items-center gap-1 text-[10px] sm:text-xs font-semibold px-2.5 py-1 rounded-full transition-colors duration-200 ${
                mode === "vo" ? "brand-gradient text-white" : "text-white/70"
              }`}
            >
              <Mic className="w-3.5 h-3.5" /> VO
            </button>
            <button
              data-testid="player-mode-novo"
              onClick={() => setMode("novo")}
              className={`flex items-center gap-1 text-[10px] sm:text-xs font-semibold px-2.5 py-1 rounded-full transition-colors duration-200 ${
                mode === "novo" ? "brand-gradient text-white" : "text-white/70"
              }`}
            >
              <Type className="w-3.5 h-3.5" /> Text
            </button>
          </div>

          <button
            data-testid="player-guide"
            onClick={() => setGuide((g) => !g)}
            className={`${ctrlBtn} w-8 h-8 ${guide ? "text-orange-400" : ""}`}
            aria-label="9:16 safe guide"
            title="Toggle 9:16 vertical-safe guide"
          >
            <Crop className="w-4 h-4" />
          </button>
          <button data-testid="player-fullscreen" onClick={goFullscreen} className={`${ctrlBtn} w-8 h-8`} aria-label="Fullscreen">
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
